import React, { useState, useCallback, useRef } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import UnitDialog from '../products/ProductDialog';
import ProductImportDialog from '../products/ProductImportDialog';
import { ActivitySquare, CheckIcon } from 'lucide-react';

interface Unit {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  symbol: string;
  unitOfMeasurement: string;
  isSynced: boolean;
  createdAt: string;
  updatedAt: string;
  onHandQuantity: number;
  expectedQuantity: number;
  itemAdditionalProperties: Record<string, any>;
  itemMinNumber?: number | null;
  itemMaxNumber?: number | null;
}

const LS_KEY = 'units-dynamic-prop-keys';

const InactiveUnitsListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [activateId, setActivateId] = useState<string | null>(null);
  const [activateUnit, setActivateUnit] = useState<Unit | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const askActivate = (unit: Unit) => {
    setActivateId(unit.id);
    setActivateUnit(unit);
  };

  // ---------- Import Products ----------
  const handleImportProducts = () => {
    setImportDialogOpen(true);
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    setRefreshKey((p) => p + 1);
    dispatch(showAlert({ message: 'Products imported successfully!', type: 'success' }));
  };

  const canonicalize = (k: string) => k?.toLowerCase().trim();

const getCaseInsensitive = (obj: Record<string, any> | undefined, canonicalKey: string) => {
  if (!obj) return null;
  const exact = obj[canonicalKey];
  if (exact !== undefined) return exact;

  const found = Object.entries(obj).find(([kk]) => canonicalize(kk) === canonicalKey);
  return found ? found[1] : null;
};

  // ---------- Format headers nicely ----------
  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const specialCases: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season',
    };
    return specialCases[key] || formatted;
  }, []);

const initialSeenKeys = (() => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    const uniq = Array.isArray(arr) ? Array.from(new Set(arr.map((k: string) => canonicalize(k)))) : [];
    return uniq;
  } catch {
    return [];
  }
})();

const handleActivate = async () => {
  if (!activateId) return;
  try {
    const res = await DataService.post(`api/Unit/${activateId}/activate`, {}); // POST with empty body
    if (res.ok) {
      dispatch(showAlert({ message: 'Unit activated successfully!', type: 'success' }));
      setRefreshKey((p) => p + 1);
    } else {
      const text = await res.text().catch(() => '');
      dispatch(showAlert({ message: `Failed to activate Unit! ${text}`, type: 'error' }));
    }
  } catch {
    dispatch(showAlert({ message: 'Failed to activate Unit!', type: 'error' }));
  } finally {
    setActivateId(null);
    setActivateUnit(null);
  }
};

const seenDynamicKeysRef = useRef<Set<string>>(new Set(initialSeenKeys));

const persistSeenKeys = (set: Set<string>) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set))); // already canonicalized
  } catch {}
};

  const buildColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const base: PaginatedDataGridColumn[] = [
        {
          field: 'barcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          groupable: false, // Barcode shouldn't be grouped
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: 'pointer', fontWeight: 'medium' }}
              onClick={() => navigate(`/units/${params.row.id}`)}
            >
              {params.value}
            </Typography>
          ),
        },
        { 
          field: 'itemName', 
          headerName: 'Item Name', 
          flex: 1.5, 
          minWidth: 160,
          groupable: true, 
        },
        { 
          field: 'symbol', 
          headerName: 'Symbol', 
          flex: 1, 
          minWidth: 120,
          groupable: true,
        },
        { 
          field: 'unitOfMeasurement', 
          headerName: 'Measurement', 
          flex: 1, 
          minWidth: 120,
          groupable: true,
        },
        { 
          field: 'expectedQuantity', 
          headerName: 'Expected', 
          width: 110, 
          type: 'number',
          groupable: true,
        },
        { 
          field: 'onHandQuantity', 
          headerName: 'On Hand', 
          width: 110, 
          type: 'number',
          groupable: true,
        },
        {
          field: 'isSynced',
          headerName: 'Synced',
          width: 100,
          groupable: true,
          renderCell: (params: GridRenderCellParams) => (
            <Chip
              label={params.value ? 'Yes' : 'No'}
              color={params.value ? 'success' : 'warning'}
              size="small"
              variant="outlined"
            />
          ),
        },
      ];

const dynamicCols = dynamicKeys.sort().map((ck) => {
  const prettyHeader = formatPropertyName(ck); 

      const col: PaginatedDataGridColumn = {
        field: ck,
        headerName: prettyHeader,
        flex: 1,
        minWidth: 110,
        valueGetter: (params) => getCaseInsensitive(params.row.itemAdditionalProperties, ck),
        sortable: true,
        groupable: true, // Additional properties can be grouped
      };

      if (ck.includes('color')) {
        col.columnType = 'color';
        col.renderCell = (params: GridRenderCellParams) => {
          if (!params.value) return <Typography color="text.secondary">-</Typography>;
          const colorValue = colorParser(params.value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: colorValue,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          );
        };
      }

      // Category/Brand/Season as chips
      if (['category', 'maincategory', 'brand', 'season'].some((f) => ck.includes(f))) {
        col.renderCell = (params: GridRenderCellParams) => {
          if (!params.value) return <Typography color="text.secondary">-</Typography>;
          return <Chip label={params.value} size="small" variant="outlined" color="primary" />;
        };
      }

      return col;
    });

      return [...base, ...dynamicCols];
    },
    [formatPropertyName, navigate]
  );

const [dynamicColumns, setDynamicColumns] = useState<PaginatedDataGridColumn[]>(
  buildColumns(Array.from(seenDynamicKeysRef.current))
);
  const mapFieldForGrouping = useCallback((field: string): string => {
    const canonicalField = canonicalize(field);
    
    if (seenDynamicKeysRef.current.has(canonicalField)) {
      return `Item.json:${canonicalField}`;
    }
    
    const fieldMapping: Record<string, string> = {
      'itemname': 'Item.Name',
      'symbol': 'Symbol',
      'unitofmeasurement': 'UnitOfMeasurement', 
      'issynced': 'IsSynced',
      'barcode': 'Barcode',
      'expectedquantity': 'ExpectedQuantity',
      'onhandquantity': 'OnHandQuantity',
    };
    
    return fieldMapping[canonicalField] || field;
  }, []);
  
  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);
      
 
        if (params.sortModel?.length > 0) {
          const { field, sort } = params.sortModel[0];
          const canonicalField = canonicalize(field);
          const isDynamic = seenDynamicKeysRef.current.has(canonicalField);
          queryParams.append('SortBy', isDynamic ? `itemAdditionalProperties.${canonicalField}` : field);
          queryParams.append('SortDescending', String(sort === 'desc'));
        }

      const endpoint = 'api/Unit/inactive';
      const res = await DataService.get(`${endpoint}?${queryParams.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch units`);
      const json = await res.json();

      let items: Unit[] = [];
      let groups: any[] = [];
      
 
        if (json?.data?.items) {
          if (Array.isArray(json.data.items)) items = json.data.items;
          else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
        } else if (Array.isArray(json?.items)) {
          items = json.items;
        
        
        const newKeys: string[] = [];
        for (const u of items) {
          if (u.itemAdditionalProperties) {
            Object.keys(u.itemAdditionalProperties).forEach((k) => {
              const ck = canonicalize(k);
              if (!seenDynamicKeysRef.current.has(ck)) {
                seenDynamicKeysRef.current.add(ck);
                newKeys.push(ck);
              }
            });
          }
        }
        
        if (newKeys.length > 0) {
          persistSeenKeys(seenDynamicKeysRef.current);
          setDynamicColumns(buildColumns(Array.from(seenDynamicKeysRef.current)));
        }
      }


      return {
        items: items,
        groups: undefined,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? (items.length),
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (json?.data?.totalCount ?? json?.totalCount ?? items.length) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [buildColumns, mapFieldForGrouping]
  );

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'import',
      title: 'Import Products',
      icon: <FileUploadIcon />,
      onClick: handleImportProducts,
      disabled: () => false,
      color: 'primary',
    },
  ];

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await DataService.delete(`api/Unit/delete/${deleteId}`, {});
      if (res.ok) {
        dispatch(showAlert({ message: 'Unit deleted successfully!', type: 'success' }));
        setRefreshKey((p) => p + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
    }
    setDeleteId(null);
  };

  const handleRowClick = (params: any) => navigate(`/Units/${params.row.id}`);

  const handleEditUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = (updatedUnit: any) => {
    dispatch(showAlert({ message: 'Unit updated successfully!', type: 'success' }));
    setRefreshKey((p) => p + 1);
    setEditDialogOpen(false);
    setSelectedUnit(null);
  };

  return (
    <>
      <PaginatedDataGrid
        key={refreshKey}
        title="Units"
        columns={dynamicColumns}
        idField="id"
        useBackendPagination
        onDataFetch={fetchUnits}
        cacheTimeout={300000}
        actions={actions}
        enableGrouping={false}
        enableGroupDrillDown={false}
        fieldMapper={mapFieldForGrouping}
        wmsMode
        // groupedUrl='api/Unit/grouped/'
        densityLevel="compact"
        striped
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        maxHeight={700}
        onRowClick={handleRowClick}
        rowActions={[
          {
            key: 'edit',
            icon: <EditIcon />,
            label: 'Edit Unit',
            color: 'primary',
            onClick: (_id: GridRowId, row: any) => handleEditUnit(row),
          },
           {
            key: 'activate',
            icon: <CheckIcon />,
            label: 'Activate',
            color: 'success',
            onClick: (_id: GridRowId, row: Unit) => askActivate(row),
          },
        ]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />

      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Unit"
        message="Are you sure you want to delete this Unit? This action cannot be undone."
      />

      <UnitDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUnit(null);
        }}
        unit={selectedUnit ? {
          ...selectedUnit,
          name: selectedUnit.itemName,
          additionalProperties: selectedUnit.itemAdditionalProperties,
          minNumber: selectedUnit.itemMinNumber,
          maxNumber: selectedUnit.itemMaxNumber,
          hasProperties: !!selectedUnit.itemAdditionalProperties && Object.keys(selectedUnit.itemAdditionalProperties).length > 0,
          propertyCount: selectedUnit.itemAdditionalProperties ? Object.keys(selectedUnit.itemAdditionalProperties).length : 0
        } : null}
        mode="edit"
        onSuccess={handleEditSuccess}
      />

      <ProductImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={handleImportSuccess}
      />

       <SimpleConfirmationDialog
        open={!!activateId}
        onClose={() => {
          setActivateId(null);
          setActivateUnit(null);
        }}
        onConfirm={handleActivate}
        title="Activate Unit"
        message={
          activateUnit
            ? `Are you sure you want to activate unit '${activateUnit.barcode}'?`
            : 'Are you sure you want to activate this unit?'
        }
      />
    </>
  );
};

export default InactiveUnitsListing;
